// React
import { useCallback, useMemo } from 'react';

// MUI Components
import { Box, Paper, Typography, Tooltip, Skeleton } from '@mui/material';
import DataGrid from '~/components/General/OlcDataGrid';

// Styles - reuse campaigns table styles for visual parity
import '../CampaignsMailDate/styles.scss';

const SingleMailDateTable = ({ mailedDate, selectedDate, templateInfo, batchDates = [] }) => {
  const hasBatches = batchDates.length > 0;

  const rows = useMemo(() => {
    if (!selectedDate || !mailedDate) return [];

    return [
      {
        id: 0,
        templateName: templateInfo?.label || 'Selected Mailer',
        productType: templateInfo?.productType || '',
        productId: templateInfo?.productId || '',
        mailDate: mailedDate,
      },
    ];
  }, [mailedDate, selectedDate, templateInfo]);

  const loading = Boolean(selectedDate && !mailedDate);
  const getCompactMailDateValue = useCallback(
    (row) => {
      if (hasBatches) return 'Batch dates';
      return row?.mailDate ? 'Mail date' : '-';
    },
    [hasBatches]
  );
  const getCompactTemplateValue = useCallback((row) => {
    return row?.templateName ? 'Template' : '-';
  }, []);
  const getCompactProductValue = useCallback((row) => {
    return row?.productType ? 'Product' : '-';
  }, []);

  const columns = useMemo(
    () => [
      {
        field: 'templateName',
        headerName: 'Template',
        flex: 1.2,
        width: 180,
        compactShrinkMinWidth: 140,
        compactShrinkPriority: 0,
        compactValueGetter: getCompactTemplateValue,
        headerAlign: 'left',
        align: 'left',
        sortable: false,
        renderCell: (params) => (
          <Tooltip title={params.row.templateName} placement="top-start">
            <Typography className="templateNameCell">{params.row.templateName}</Typography>
          </Tooltip>
        ),
      },
      {
        field: 'productType',
        headerName: 'Product',
        flex: 1.2,
        width: 180,
        compactShrinkMinWidth: 90,
        compactShrinkPriority: 0,
        compactValueGetter: getCompactProductValue,
        headerAlign: 'left',
        align: 'left',
        sortable: false,
        renderCell: (params) => (
          <Typography className="templateNameCell">{params.row.productType || '-'}</Typography>
        ),
      },
      {
        field: 'mailDate',
        headerName: 'Mail Date',
        flex: 1.4,
        minWidth: 250,
        compactMaxWidth: 250,
        compactShrinkMinWidth: 145,
        compactShrinkPriority: 1,
        compactValueGetter: getCompactMailDateValue,
        headerAlign: 'left',
        align: 'left',
        sortable: false,
        renderCell: (params) => {
          if (hasBatches) {
            return (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px',
                  width: '100%',
                  alignItems: 'flex-start',
                }}
              >
                {batchDates.map(({ batch, date }) => (
                  <Typography key={batch} className="dateText" sx={{ lineHeight: '1.5' }}>
                    Batch {batch} - {date}
                  </Typography>
                ))}
              </Box>
            );
          }
          return <Typography className="dateText">{params.row.mailDate}</Typography>;
        },
      },
    ],
    [
      hasBatches,
      batchDates,
      getCompactMailDateValue,
      getCompactTemplateValue,
      getCompactProductValue,
    ]
  );

  const LoadingSkeleton = () => (
    <Box className="skeletonContainer">
      {[1].map((item) => (
        <Box key={item} className="skeletonRow">
          <Skeleton variant="text" width="30%" height={24} />
          <Skeleton variant="text" width="30%" height={24} />
          <Skeleton variant="rectangular" width={80} height={20} />
        </Box>
      ))}
    </Box>
  );

  if (loading) {
    return (
      <Box className="campaignsMailDateContainer">
        <Paper className="tableContainer">
          <LoadingSkeleton />
        </Paper>
      </Box>
    );
  }

  return (
    <Box className="campaignsMailDateContainer">
      {selectedDate && (
        <Box
          className="tableContainer"
          sx={{
            scrollbarColor: 'var(--olc-scrollbar-thumb) var(--olc-scrollbar-track)',
            scrollbarWidth: 'thin',
            '&::-webkit-scrollbar': {
              width: '14px',
            },
          }}
        >
          {rows.length > 0 && (
            <Box className="dataGridWrapper">
              <DataGrid
                rows={rows}
                columns={columns}
                autoHeight
                compactColumns={false}
                measureContentWidth={false}
                preserveColumnLayout
                getRowId={(row) => row?.id}
                hideFooter
                hideFooterPagination
                hideFooterSelectedRowCount
                disableColumnMenu
                disableColumnFilter
                disableColumnSelector
                disableDensitySelector
                disableRowSelectionOnClick
                className="mailDateTable"
                getRowHeight={() => (hasBatches ? 'auto' : 56)}
                sx={{
                  '& .MuiDataGrid-root': { border: 'none' },
                  '& .MuiDataGrid-main': { borderRadius: 'inherit', overflow: 'hidden' },
                  '& .MuiDataGrid-cell': {
                    alignItems: 'flex-start',
                    paddingTop: hasBatches ? '12px' : undefined,
                  },
                }}
              />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default SingleMailDateTable;
